// User-level geolocation: capture once after login, refresh silently
// per session, fall back to IP if the user denied.
//
// Why this exists: IP-based geolocation routes through the user's ISP
// gateway, which often lives in a different city — e.g. a Hyderabad
// user appearing as "Delhi". Browser geolocation gives the actual
// physical location.
//
// Two-strike consent flow:
//   Attempt 1: fired by RenderOnAuthenticated 1.5s after login
//   Attempt 2: fired by ProjectDetailsV3 on mount, if attempt 1 was denied
//   After 2nd denial: 7-day silent cool-down (per device — localStorage)
//
// Once consent is granted, the browser remembers it permanently. We never
// prompt again — every analytics session silently re-fetches the latest
// GPS position so travellers' data stays accurate (Hyderabad → Dubai →
// New York reflects in real time without any user interaction).

const CACHE_KEY = "vrGeo:state";
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_ATTEMPTS = 2;

export interface UserGeo {
  city: string | null;
  region: string | null;
  country: string | null;
  country_code: string | null;
  // Raw GPS coordinates the city was reverse-geocoded from. Carried
  // through to the session payload so analytics can plot pins at the
  // actual position instead of the city centre.
  latitude: number | null;
  longitude: number | null;
}

interface GeoState {
  consent: "granted" | "denied" | null;
  attempts: number;
  cooldownUntil: number | null;
}

const readState = (): GeoState => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return { consent: null, attempts: 0, cooldownUntil: null };
    const parsed = JSON.parse(raw);
    return {
      consent: parsed.consent ?? null,
      attempts: typeof parsed.attempts === "number" ? parsed.attempts : 0,
      cooldownUntil:
        typeof parsed.cooldownUntil === "number" ? parsed.cooldownUntil : null,
    };
  } catch {
    return { consent: null, attempts: 0, cooldownUntil: null };
  }
};

const writeState = (s: GeoState) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(s));
  } catch {
    // localStorage disabled or full — best effort
  }
};

// Approximate viewport area in km² from Google's NE/SW corners.
const viewportAreaKm2 = (v: any): number | null => {
  if (!v?.northeast || !v?.southwest) return null;
  const dLat = (v.northeast.lat - v.southwest.lat) * 111;
  const midLat = (v.northeast.lat + v.southwest.lat) / 2;
  const dLng =
    (v.northeast.lng - v.southwest.lng) *
    111 *
    Math.cos((midLat * Math.PI) / 180);
  return Math.abs(dLat * dLng);
};

// Reverse-geocode lat/lng → full UserGeo via Google Maps API.
const reverseGeocodeFull = async (
  lat: number,
  lng: number,
): Promise<UserGeo | null> => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) return null;
  try {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;
    const res = await fetch(url);
    const data = await res.json();
    if (data?.status !== "OK" || !Array.isArray(data?.results)) return null;
    let city: string | null = null;
    let region: string | null = null;
    let country: string | null = null;
    let country_code: string | null = null;

    // City: prefer the locality from the LAST result that carries one.
    // Google orders `results` most-specific → most-general. For metro
    // suburbs that Google still classifies as their own locality (e.g.
    // Manikonda inside Hyderabad), the early results name the suburb
    // and a later one names the proper city. Take the last.
    const localityResults = data.results.filter((r: any) =>
      (r.address_components || []).some((c: any) =>
        c.types?.includes("locality"),
      ),
    );
    const cityResult = localityResults[localityResults.length - 1];
    if (cityResult) {
      const locality = (cityResult.address_components || []).find((c: any) =>
        c.types?.includes("locality"),
      );
      if (locality?.long_name) city = locality.long_name;
    }

    // Peri-urban villages (e.g. Avilali, ~5km outside Tirupati) get
    // tagged by Google as their own locality with a tiny viewport.
    // When that happens, broaden to admin_area_level_3 — in India
    // that's typically the surrounding city / revenue division
    // ("Tirupati"). Skipped when locality is already city-sized
    // (Hyderabad's viewport is ~2300km², so Manikonda → Hyderabad
    // stays Hyderabad and doesn't slide to "Rangareddy" district).
    const cityVpKm2 = viewportAreaKm2(cityResult?.geometry?.viewport);
    if (cityVpKm2 !== null && cityVpKm2 < 50) {
      const aa3Result = data.results.find(
        (r: any) =>
          Array.isArray(r.types) &&
          r.types.includes("administrative_area_level_3"),
      );
      const aa3 =
        aa3Result &&
        (aa3Result.address_components || []).find((c: any) =>
          c.types?.includes("administrative_area_level_3"),
        );
      if (aa3?.long_name && aa3.long_name !== city) {
        city = aa3.long_name;
      }
    }

    // region / country come from any result's components.
    for (const result of data.results) {
      for (const c of (result.address_components || []) as any[]) {
        if (!region && c.types?.includes("administrative_area_level_1"))
          region = c.long_name;
        if (!country && c.types?.includes("country")) {
          country = c.long_name;
          country_code = c.short_name;
        }
      }
      if (region && country) break;
    }
    // Sparse-coverage fallback: admin_area_level_2 if no locality at all.
    if (!city) {
      for (const result of data.results) {
        const a = (result.address_components || []).find((c: any) =>
          c.types?.includes("administrative_area_level_2"),
        );
        if (a?.long_name) {
          city = a.long_name;
          break;
        }
      }
    }
    return { city, region, country, country_code, latitude: lat, longitude: lng };
  } catch {
    return null;
  }
};

// Browser-level permission state, queried via the Permissions API. Returns
// null if the API is unavailable (older Safari, Firefox in some modes) — we
// then fall back to trusting our cached state.
const browserPermissionState = async (): Promise<
  "granted" | "denied" | "prompt" | null
> => {
  if (typeof navigator === "undefined" || !(navigator as any).permissions?.query) {
    return null;
  }
  try {
    const result = await (navigator as any).permissions.query({
      name: "geolocation",
    });
    return result?.state ?? null;
  } catch {
    return null;
  }
};

// Browser is the source of truth. If the user changed location permission
// via 🔒 → Site Settings since we last cached, sync our state to match
// before deciding whether to skip, prompt, or silently fetch.
const reconcileWithBrowser = async (state: GeoState): Promise<GeoState> => {
  const browserState = await browserPermissionState();
  if (browserState === null) return state;

  if (browserState === "granted" && state.consent !== "granted") {
    const next: GeoState = {
      consent: "granted",
      attempts: state.attempts,
      cooldownUntil: null,
    };
    writeState(next);
    return next;
  }
  if (browserState === "denied" && state.consent !== "denied") {
    const next: GeoState = {
      consent: "denied",
      attempts: MAX_ATTEMPTS,
      cooldownUntil: Date.now() + COOLDOWN_MS,
    };
    writeState(next);
    return next;
  }
  if (
    browserState === "prompt" &&
    (state.consent !== null || state.cooldownUntil)
  ) {
    // User reset permission to "Ask" — give them a fresh chance.
    const next: GeoState = { consent: null, attempts: 0, cooldownUntil: null };
    writeState(next);
    return next;
  }
  return state;
};

type PositionResult =
  | { lat: number; lng: number }
  | { error: "permission_denied" | "other" };

// Promise wrapper around navigator.geolocation.getCurrentPosition.
// Normalises the error union so callers can switch on a string.
const getBrowserPosition = (): Promise<PositionResult> => {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve({ error: "other" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) =>
        resolve({ error: err.code === 1 ? "permission_denied" : "other" }),
      {
        enableHighAccuracy: false,
        timeout: 30_000,
        maximumAge: 60 * 60 * 1000,
      },
    );
  });
};

// SILENT path — never prompts the user. Returns the user's latest GPS
// location reverse-geocoded to city, or null if consent isn't granted.
// Used by useMatterportAnalytics per session; analytics must never
// interrupt the tour with a permission prompt.
let silentInFlight: Promise<UserGeo | null> | null = null;
export const fetchFreshUserGeo = async (): Promise<UserGeo | null> => {
  if (silentInFlight) return silentInFlight;
  silentInFlight = (async () => {
    let state = readState();
    state = await reconcileWithBrowser(state);
    if (state.consent !== "granted") return null;
    const pos = await getBrowserPosition();
    if ("error" in pos) {
      // Permission revoked at browser level since we cached "granted".
      // Sync our state and bail.
      if (pos.error === "permission_denied") {
        writeState({
          consent: "denied",
          attempts: MAX_ATTEMPTS,
          cooldownUntil: Date.now() + COOLDOWN_MS,
        });
      }
      return null;
    }
    return reverseGeocodeFull(pos.lat, pos.lng);
  })();
  try {
    return await silentInFlight;
  } finally {
    silentInFlight = null;
  }
};

// PROMPTING path — may show the browser's location permission UI.
// Handles the 2-strike + 7-day cool-down rules internally. Safe to call
// from multiple entry points (login, ProjectDetails) without worrying
// about over-asking.
let promptInFlight: Promise<UserGeo | null> | null = null;
export const promptUserGeo = async (): Promise<UserGeo | null> => {
  if (promptInFlight) return promptInFlight;
  promptInFlight = (async () => {
    let state = readState();
    state = await reconcileWithBrowser(state);

    // Already granted (cached or just reconciled) → silent refresh, no prompt.
    if (state.consent === "granted") return fetchFreshUserGeo();

    // 2-strike limit hit → wait out cool-down.
    if (
      state.consent === "denied" &&
      state.attempts >= MAX_ATTEMPTS &&
      state.cooldownUntil &&
      state.cooldownUntil > Date.now()
    ) {
      return null;
    }

    const pos = await getBrowserPosition();
    if ("error" in pos) {
      const attempts = Math.min(state.attempts + 1, MAX_ATTEMPTS);
      const cooldownUntil =
        attempts >= MAX_ATTEMPTS ? Date.now() + COOLDOWN_MS : null;
      writeState({ consent: "denied", attempts, cooldownUntil });
      return null;
    }
    const geo = await reverseGeocodeFull(pos.lat, pos.lng);
    writeState({
      consent: "granted",
      attempts: state.attempts + 1,
      cooldownUntil: null,
    });
    return geo;
  })();

  try {
    return await promptInFlight;
  } finally {
    promptInFlight = null;
  }
};
